import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ESCALATION_NOTIFICATION_QUERY_KEYS } from '@/hooks/useEscalationNotifications';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import type {
  SessionEscalation,
  SessionEscalationWithUsers,
  CreateEscalationPayload,
  RespondEscalationPayload,
  EscalationStatus,
} from '@/types/support.types';

const ESCALATIONS_QUERY_KEY = 'session-escalations';
const PENDING_ESCALATIONS_QUERY_KEY = 'pending-escalations';

// ============================================
// Hook for session-specific escalations
// ============================================

export function useSessionEscalations(sessionId: string | null) {
  const { data: currentAppUser } = useCurrentAppUser();
  const queryClient = useQueryClient();

  // Query: escalations for current session
  const escalationsQuery = useQuery({
    queryKey: [ESCALATIONS_QUERY_KEY, sessionId],
    queryFn: async (): Promise<SessionEscalationWithUsers[]> => {
      if (!sessionId) return [];

      const { data, error } = await supabase
        .from('session_escalations')
        .select(`
          *,
          escalated_by_user:app_users!session_escalations_escalated_by_fkey(
            id, display_name, first_name, last_name
          ),
          escalated_to_user:app_users!session_escalations_escalated_to_fkey(
            id, display_name, first_name, last_name
          )
        `)
        .eq('session_id', sessionId)
        .order('escalated_at', { ascending: false });

      if (error) throw error;
      return (data || []) as SessionEscalationWithUsers[];
    },
    enabled: !!sessionId,
    staleTime: 30 * 1000,
  });

  // Mutation: create escalation
  const createEscalationMutation = useMutation({
    mutationFn: async ({ 
      session_id, 
      escalated_to, 
      question,
      priority = 'normal',
      department_id 
    }: CreateEscalationPayload) => {
      if (!currentAppUser?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('session_escalations')
        .insert({
          session_id,
          escalated_by: currentAppUser.id,
          escalated_to,
          question,
          priority,
          department_id,
          status: 'pending' as any,
          escalated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ESCALATIONS_QUERY_KEY, variables.session_id] });
      queryClient.invalidateQueries({ queryKey: [PENDING_ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.pending] });
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
    },
  });

  // Mutation: respond to escalation
  const respondEscalationMutation = useMutation({
    mutationFn: async ({ escalation_id, response }: RespondEscalationPayload) => {
      const { data, error } = await supabase
        .from('session_escalations')
        .update({
          response,
          status: 'responded' as any,
          responded_at: new Date().toISOString(),
        })
        .eq('id', escalation_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PENDING_ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.pending] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.responded] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
    },
    onError: (error) => {
      console.error('[respondEscalation] Mutation error:', error);
    },
  });

  // Mutation: cancel escalation
  const cancelEscalationMutation = useMutation({
    mutationFn: async ({ escalation_id, reason }: { escalation_id: string; reason: string }) => {
      if (!currentAppUser?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('session_escalations')
        .update({
          status: 'cancelled' as any,
          response: reason,
        })
        .eq('id', escalation_id)
        .eq('escalated_by', currentAppUser.id)
        .eq('status', 'pending')
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PENDING_ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.pending] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
      queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
    },
  });

  return {
    // Query state
    escalations: escalationsQuery.data || [],
    isLoading: escalationsQuery.isLoading,
    isError: escalationsQuery.isError,
    error: escalationsQuery.error,
    refetch: escalationsQuery.refetch,

    // Mutations
    createEscalation: createEscalationMutation.mutateAsync,
    isCreating: createEscalationMutation.isPending,

    respondEscalation: respondEscalationMutation.mutateAsync,
    isResponding: respondEscalationMutation.isPending,

    cancelEscalation: cancelEscalationMutation.mutateAsync,
    isCancelling: cancelEscalationMutation.isPending,
  };
}

// ============================================
// Hook for pending escalations (Legal inbox)
// ============================================

export interface PendingEscalationWithSession extends SessionEscalationWithUsers {
  session: {
    id: string;
    contact_id: string;
    status: string;
  } | null;
}

export function usePendingEscalations() {
  const { data: currentAppUser } = useCurrentAppUser();
  const queryClient = useQueryClient();

  // Query: pending escalations assigned to current user
  const pendingQuery = useQuery({
    queryKey: [PENDING_ESCALATIONS_QUERY_KEY, currentAppUser?.id],
    queryFn: async (): Promise<PendingEscalationWithSession[]> => {
      if (!currentAppUser?.id) return [];

      const { data, error } = await supabase
        .from('session_escalations')
        .select(`
          *,
          session:support_sessions(id, contact_id, status),
          escalated_by_user:app_users!session_escalations_escalated_by_fkey(
            id, display_name, first_name, last_name
          ),
          escalated_to_user:app_users!session_escalations_escalated_to_fkey(
            id, display_name, first_name, last_name
          )
        `)
        .eq('escalated_to', currentAppUser.id)
        .eq('status', 'pending')
        .order('escalated_at', { ascending: true });

      if (error) throw error;
      return (data || []) as PendingEscalationWithSession[];
    },
    enabled: !!currentAppUser?.id,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Mutation: respond to escalation
  const respondEscalationMutation = useMutation({
    mutationFn: async ({ escalation_id, response }: RespondEscalationPayload) => {
      const { data, error } = await supabase
        .from('session_escalations')
        .update({
          response,
          status: 'responded' as any,
          responded_at: new Date().toISOString(),
        })
        .eq('id', escalation_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PENDING_ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ESCALATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.pending] });
      queryClient.invalidateQueries({ queryKey: [ESCALATION_NOTIFICATION_QUERY_KEYS.responded] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-filtered'] });
    },
  });

  return {
    pendingEscalations: pendingQuery.data || [],
    isLoading: pendingQuery.isLoading,
    pendingCount: pendingQuery.data?.length || 0,

    respondEscalation: respondEscalationMutation.mutateAsync,
    isResponding: respondEscalationMutation.isPending,
  };
}

// ============================================
// Constants for UI
// ============================================

export const ESCALATION_STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-500' },
  responded: { label: 'Respondido', color: 'bg-green-500' },
  expired: { label: 'Expirado', color: 'bg-red-500' },
  cancelled: { label: 'Cancelado', color: 'bg-gray-500' },
} as const;

export const RESOLUTION_TYPE_CONFIG = {
  resolved: { label: 'Resolvido', color: 'bg-green-500' },
  no_response: { label: 'Sem Resposta', color: 'bg-yellow-500' },
  duplicate: { label: 'Duplicado', color: 'bg-blue-500' },
  spam: { label: 'Spam', color: 'bg-red-500' },
} as const;

// Known department IDs
export const LEGAL_DEPARTMENT_ID = '881d77a5-ef79-45d9-beee-cf4224b1578a';

// Departments available for escalation (excludes CS)
export const ESCALATION_DEPARTMENT_OPTIONS = [
  { id: '881d77a5-ef79-45d9-beee-cf4224b1578a', name: 'Jurídico', code: 'legal' },
  { id: '9c0230ca-57f3-4228-bc04-658d795e60e8', name: 'Comercial', code: 'sales' },
  { id: 'dcef58a7-faac-4060-84e0-af9c0daef539', name: 'Financeiro', code: 'finance' },
  { id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890', name: 'Atendimento', code: 'customer_service' },
] as const;
